import React, { useState, useEffect } from 'react';
import { DrawingElement, ConnectionFillType, PartAttribute } from '@/app/types/cad';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import { Separator } from '@/app/components/ui/separator';
import { Slider } from '@/app/components/ui/slider';
import { Switch } from '@/app/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { AttributeManager } from '@/app/components/AttributeManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PropertiesPanelProps {
  selectedElements: DrawingElement[];
  onElementUpdate: (id: string, updates: Partial<DrawingElement>) => void;
  onBatchUpdate?: (updates: Array<{ id: string; changes: Partial<DrawingElement> }>) => void;
  // Attribute management
  attributes?: PartAttribute[];
  elements?: DrawingElement[];
  onAttributeAdd?: (attribute: Omit<PartAttribute, 'id'>) => void;
  onAttributeUpdate?: (id: string, updates: Partial<PartAttribute>) => void;
  onAttributeDelete?: (id: string) => void;
  gridSize?: number;
}

export function PropertiesPanel({ selectedElements, onElementUpdate, onBatchUpdate, attributes, elements, onAttributeAdd, onAttributeUpdate, onAttributeDelete, gridSize = 20 }: PropertiesPanelProps) {
  const { t } = useLanguage();
  // Local states for slider values when multiple different values exist
  const [localWidth, setLocalWidth] = useState<number | null>(null);
  const [localStrokeWidth, setLocalStrokeWidth] = useState<number | null>(null);
  const [localFontSize, setLocalFontSize] = useState<number | null>(null);
  const [localRotation, setLocalRotation] = useState<number | null>(null);
  const [localTextSize, setLocalTextSize] = useState<number | null>(null);
  const [localOffset, setLocalOffset] = useState<number | null>(null);
  const [localDiameter, setLocalDiameter] = useState<number | null>(null);
  const [localConnectionStrokeWidth, setLocalConnectionStrokeWidth] = useState<number | null>(null);

  // State for line length input
  const [lineLengthInput, setLineLengthInput] = useState<string | null>(null);

  // Reset local states when selection changes
  useEffect(() => {
    setLocalWidth(null);
    setLocalStrokeWidth(null);
    setLocalFontSize(null);
    setLocalRotation(null);
    setLocalTextSize(null);
    setLocalOffset(null);
    setLocalDiameter(null);
    setLocalConnectionStrokeWidth(null);
  }, [selectedElements]);

  // Reset line length input ONLY when the selected element ID changes
  useEffect(() => {
    setLineLengthInput(null);
  }, [selectedElements[0]?.id]);

  if (selectedElements.length === 0) {
    // Show Attribute Manager even when no elements are selected
    if (attributes && elements && onAttributeAdd && onAttributeUpdate && onAttributeDelete) {
      return (
        <div className="w-full h-full bg-gray-50 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <AttributeManager
              attributes={attributes}
              elements={elements}
              onAttributeAdd={onAttributeAdd}
              onAttributeUpdate={onAttributeUpdate}
              onAttributeDelete={onAttributeDelete}
            />
          </div>
        </div>
      );
    }
    
    return (
      <div className="w-full h-full bg-gray-50 p-4">
        <p className="text-sm text-gray-500">{t('editor.properties.noSelection')}</p>
      </div>
    );
  }

  // Update all selected elements
  const updateAllSelected = (updates: Partial<DrawingElement>) => {
    selectedElements.forEach(el => {
      onElementUpdate(el.id, updates);
    });
  };

  // Check if all selected elements have the same type
  const elementTypes = [...new Set(selectedElements.map(el => el.type))];
  const isSameType = elementTypes.length === 1;
  const firstElement = selectedElements[0];

  // Get common value for a property (or undefined if values differ)
  const getCommonValue = (prop: keyof DrawingElement): any => {
    const firstValue = (firstElement as any)[prop];
    const allSame = selectedElements.every(el => (el as any)[prop] === firstValue);
    return allSame ? firstValue : undefined;
  };

  // Check if all elements have a specific property
  const hasProperty = (prop: string): boolean => {
    return selectedElements.every(el => prop in el);
  };

  return (
    <div className="w-full h-full bg-gray-50 p-4 overflow-y-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Eigenschaften</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Auswahl</Label>
            <p className="text-sm font-medium">
              {selectedElements.length} {selectedElements.length === 1 ? 'Element' : 'Elemente'}
              {isSameType && ` (${firstElement.type})`}
            </p>
          </div>

          <Separator />

          {/* Common properties for lines, circles, arcs, splines */}
          {(isSameType && (firstElement.type === 'line' || firstElement.type === 'circle' || 
            firstElement.type === 'arc' || firstElement.type === 'spline')) && (
            <>
              {/* Line Length Input - Raw Units */}
              {isSameType && firstElement.type === 'line' && selectedElements.length === 1 && (
                <div className="space-y-2">
                  <Label htmlFor="line-length-input" className="text-xs">
                    Länge
                  </Label>
                  <Input
                    id="line-length-input"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={lineLengthInput ?? (() => {
                      const line = firstElement as any;
                      const du = line.end.u - line.start.u;
                      const dv = line.end.v - line.start.v;
                      // Display length in real-world units (mm)
                      return (Math.hypot(du, dv) * gridSize).toFixed(2);
                    })()}
                    onChange={(e) => setLineLengthInput(e.target.value)}
                    onBlur={() => {
                      if (lineLengthInput !== null) {
                        const val = parseFloat(lineLengthInput.replace(',', '.'));
                        if (!isNaN(val) && val >= 0) {
                          const line = firstElement as any;
                          const du = line.end.u - line.start.u;
                          const dv = line.end.v - line.start.v;
                          const currentLenGrid = Math.hypot(du, dv);
                          
                          if (currentLenGrid > 1e-9) {
                            // Convert input (mm) to grid units
                            const targetLenGrid = val / gridSize;
                            const ratio = targetLenGrid / currentLenGrid;
                            
                            const newEnd = {
                              u: line.start.u + du * ratio,
                              v: line.start.v + dv * ratio
                            };
                            onElementUpdate(line.id, { end: newEnd });
                          }
                        }
                        setLineLengthInput(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        setLineLengthInput(null);
                        e.currentTarget.blur();
                      }
                    }}
                    className="h-8 text-xs"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="common-color" className="text-xs">
                  Farbe
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="common-color"
                    type="color"
                    value={getCommonValue('color') || '#000000'}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="w-16 h-8"
                  />
                  <Input
                    type="text"
                    value={getCommonValue('color') || ''}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    placeholder={getCommonValue('color') === undefined ? 'Mehrere Werte' : ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Strichstärke: {localWidth !== null ? `${localWidth}px` : (getCommonValue('width') !== undefined ? `${getCommonValue('width')}px` : 'Mehrere Werte')}
                </Label>
                <Slider
                  value={[localWidth ?? getCommonValue('width') ?? 5]}
                  onValueChange={([value]) => {
                    setLocalWidth(value);
                    updateAllSelected({ width: value });
                  }}
                  min={1}
                  max={10}
                  step={0.5}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="line-style" className="text-xs">
                  Linienart
                </Label>
                <Select
                  value={getCommonValue('lineStyle') || 'solid'}
                  onValueChange={(value: 'solid' | 'dashed' | 'dotted' | 'dashdot') => 
                    updateAllSelected({ lineStyle: value })
                  }
                >
                  <SelectTrigger id="line-style" className="h-8">
                    <SelectValue placeholder={getCommonValue('lineStyle') === undefined ? 'Mehrere Werte' : ''} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">━━━ Durchgehend</SelectItem>
                    <SelectItem value="dashed">╌╌╌ Gestrichelt</SelectItem>
                    <SelectItem value="dotted">┄┄┄ Punktiert</SelectItem>
                    <SelectItem value="dashdot">╌·╌ Strichpunktiert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Shape-specific properties */}
          {isSameType && firstElement.type === 'shape' && (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="shape-filled" className="text-xs">
                  Gefüllt
                </Label>
                <Switch
                  id="shape-filled"
                  checked={getCommonValue('filled') ?? true}
                  onCheckedChange={(checked) => updateAllSelected({ filled: checked })}
                />
              </div>

              {getCommonValue('filled') && (
                <div className="space-y-2">
                  <Label htmlFor="fill-color" className="text-xs">
                    Füllfarbe
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="fill-color"
                      type="color"
                      value={getCommonValue('fillColor') || '#cccccc'}
                      onChange={(e) => updateAllSelected({ fillColor: e.target.value })}
                      className="w-16 h-8"
                    />
                    <Input
                      type="text"
                      value={getCommonValue('fillColor') || ''}
                      onChange={(e) => updateAllSelected({ fillColor: e.target.value })}
                      className="flex-1 h-8 text-xs"
                      placeholder={getCommonValue('fillColor') === undefined ? 'Mehrere Werte' : ''}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="stroke-color" className="text-xs">
                  Randfarbe
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="stroke-color"
                    type="color"
                    value={getCommonValue('strokeColor') || '#000000'}
                    onChange={(e) => updateAllSelected({ strokeColor: e.target.value })}
                    className="w-16 h-8"
                  />
                  <Input
                    type="text"
                    value={getCommonValue('strokeColor') || ''}
                    onChange={(e) => updateAllSelected({ strokeColor: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    placeholder={getCommonValue('strokeColor') === undefined ? 'Mehrere Werte' : ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Randstärke: {getCommonValue('strokeWidth') !== undefined ? `${getCommonValue('strokeWidth')}px` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('strokeWidth') || 2]}
                  onValueChange={([value]) => updateAllSelected({ strokeWidth: value })}
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              </div>
            </>
          )}

          {/* Text-specific properties */}
          {isSameType && firstElement.type === 'text' && (
            <>
              {selectedElements.length === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="text-content" className="text-xs">
                      Text
                    </Label>
                    <Input
                      id="text-content"
                      type="text"
                      value={firstElement.text}
                      onChange={(e) => onElementUpdate(firstElement.id, { text: e.target.value })}
                      className="h-8 text-xs"
                      placeholder="Text eingeben..."
                    />
                  </div>

                  {/* Attribute insertion buttons */}
                  {attributes && attributes.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs">
                        Attribute einfügen
                      </Label>
                      <div className="flex flex-wrap gap-1">
                        {attributes.map((attr) => (
                          <button
                            key={attr.id}
                            type="button"
                            onClick={() => {
                              const currentText = firstElement.text || '';
                              const newText = currentText + `{${attr.name}}`;
                              onElementUpdate(firstElement.id, { text: newText });
                            }}
                            className="px-2 py-1 rounded text-xs border hover:bg-black/5 transition-colors"
                            style={{
                              borderColor: 'var(--md-outline-variant)',
                              fontSize: '10px',
                            }}
                            title={`Fügt {${attr.name}} ein`}
                          >
                            <span className="font-mono">{'{' + attr.name + '}'}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
                        Klicke auf ein Attribut, um es am Ende des Textes einzufügen
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="text-color" className="text-xs">
                  Farbe
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="text-color"
                    type="color"
                    value={getCommonValue('color') || '#000000'}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="w-16 h-8"
                  />
                  <Input
                    type="text"
                    value={getCommonValue('color') || ''}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    placeholder={getCommonValue('color') === undefined ? 'Mehrere Werte' : ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Schriftgröße: {getCommonValue('fontSize') !== undefined ? `${getCommonValue('fontSize')}px` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('fontSize') || 16]}
                  onValueChange={([value]) => updateAllSelected({ fontSize: value })}
                  min={8}
                  max={72}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Rotation: {getCommonValue('rotation') !== undefined ? `${Math.round((getCommonValue('rotation') * 180) / Math.PI)}°` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('rotation') || 0]}
                  onValueChange={([value]) => updateAllSelected({ rotation: value })}
                  min={0}
                  max={Math.PI * 2}
                  step={Math.PI / 12}
                />
              </div>
            </>
          )}

          {/* Dimension-specific properties */}
          {isSameType && firstElement.type === 'dimension' && (
            <>
              {selectedElements.length === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="dimension-length-input" className="text-xs">
                      Länge (mm)
                    </Label>
                    <Input
                      id="dimension-length-input"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={lineLengthInput ?? (() => {
                        const dim = firstElement as any;
                        const du = dim.end.u - dim.start.u;
                        const dv = dim.end.v - dim.start.v;
                        // Display length in real-world units (mm)
                        return (Math.hypot(du, dv) * gridSize).toFixed(2);
                      })()}
                      onChange={(e) => setLineLengthInput(e.target.value)}
                      onBlur={() => {
                        if (lineLengthInput !== null) {
                          const val = parseFloat(lineLengthInput.replace(',', '.'));
                          if (!isNaN(val) && val >= 0) {
                            const dim = firstElement as any;
                            const du = dim.end.u - dim.start.u;
                            const dv = dim.end.v - dim.start.v;
                            const currentLenGrid = Math.hypot(du, dv);
                            
                            if (currentLenGrid > 1e-9) {
                              // Convert input (mm) to grid units
                              const targetLenGrid = val / gridSize;
                              const ratio = targetLenGrid / currentLenGrid;
                              
                              const newEnd = {
                                u: dim.start.u + du * ratio,
                                v: dim.start.v + dv * ratio
                              };
                              
                              // Update geometry AND the text value
                              onElementUpdate(dim.id, { 
                                end: newEnd,
                                value: val.toFixed(dim.decimalPlaces ?? 1)
                              });
                            }
                          }
                          setLineLengthInput(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        } else if (e.key === 'Escape') {
                          setLineLengthInput(null);
                          e.currentTarget.blur();
                        }
                      }}
                      className="h-8 text-xs font-mono bg-yellow-50/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dimension-unit" className="text-xs">
                      Einheit
                    </Label>
                    <Input
                      id="dimension-unit"
                      type="text"
                      value={firstElement.unit}
                      onChange={(e) => onElementUpdate(firstElement.id, { unit: e.target.value })}
                      className="h-8 text-xs"
                      placeholder="mm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dimension-decimal-places" className="text-xs">
                      Nachkommastellen
                    </Label>
                    <Select
                      value={String(firstElement.decimalPlaces ?? 1)}
                      onValueChange={(value) => 
                        onElementUpdate(firstElement.id, { decimalPlaces: parseInt(value) })
                      }
                    >
                      <SelectTrigger id="dimension-decimal-places" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (100)</SelectItem>
                        <SelectItem value="1">1 (100.0)</SelectItem>
                        <SelectItem value="2">2 (100.00)</SelectItem>
                        <SelectItem value="3">3 (100.000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="dimension-color" className="text-xs">
                  Farbe
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="dimension-color"
                    type="color"
                    value={getCommonValue('color') || '#2196F3'}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="w-16 h-8"
                  />
                  <Input
                    type="text"
                    value={getCommonValue('color') || ''}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    placeholder={getCommonValue('color') === undefined ? 'Mehrere Werte' : ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Textgröße: {getCommonValue('textSize') !== undefined ? `${getCommonValue('textSize')}px` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('textSize') || 14]}
                  onValueChange={([value]) => updateAllSelected({ textSize: value })}
                  min={8}
                  max={72}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Abstand: {getCommonValue('offset') !== undefined ? getCommonValue('offset') : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('offset') || 2]}
                  onValueChange={([value]) => updateAllSelected({ offset: value })}
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Linienstärke: {getCommonValue('lineWidth') !== undefined ? `${getCommonValue('lineWidth')}px` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('lineWidth') || 1]}
                  onValueChange={([value]) => updateAllSelected({ lineWidth: value })}
                  min={0.5}
                  max={5}
                  step={0.5}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Label htmlFor="dimension-show-unit" className="text-xs">
                  Einheit anzeigen
                </Label>
                <Switch
                  id="dimension-show-unit"
                  checked={getCommonValue('showUnit') ?? true}
                  onCheckedChange={(checked) => updateAllSelected({ showUnit: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="dimension-show-line" className="text-xs">
                  Bemaßungslinie anzeigen
                </Label>
                <Switch
                  id="dimension-show-line"
                  checked={getCommonValue('showLine') ?? true}
                  onCheckedChange={(checked) => updateAllSelected({ showLine: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="dimension-show-arrows" className="text-xs">
                  Pfeile anzeigen
                </Label>
                <Switch
                  id="dimension-show-arrows"
                  checked={getCommonValue('showArrows') ?? true}
                  onCheckedChange={(checked) => updateAllSelected({ showArrows: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Pfeilgröße: {getCommonValue('arrowSize') !== undefined ? getCommonValue('arrowSize') : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('arrowSize') || 8]}
                  onValueChange={([value]) => updateAllSelected({ arrowSize: value })}
                  min={4}
                  max={32}
                  step={1}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="dimension-arrow-filled" className="text-xs">
                  Pfeile gefüllt
                </Label>
                <Switch
                  id="dimension-arrow-filled"
                  checked={getCommonValue('arrowFilled') ?? false}
                  onCheckedChange={(checked) => updateAllSelected({ arrowFilled: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dimension-arrow-position" className="text-xs">
                  Pfeilposition
                </Label>
                <Select
                  value={getCommonValue('arrowPosition') || 'inside'}
                  onValueChange={(value: 'inside' | 'outside') => 
                    updateAllSelected({ arrowPosition: value })
                  }
                >
                  <SelectTrigger id="dimension-arrow-position" className="h-8">
                    <SelectValue placeholder={getCommonValue('arrowPosition') === undefined ? 'Mehrere Werte' : ''} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inside">Innen (--|&lt;--&gt;|--)</SelectItem>
                    <SelectItem value="outside">Außen (-&gt;|---|&lt;-)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Connection-specific properties */}
          {isSameType && firstElement.type === 'connection' && (
            <>
              {selectedElements.length === 1 && (
                <div className="space-y-2">
                  <Label htmlFor="connection-label" className="text-xs">
                    Label
                  </Label>
                  <Input
                    id="connection-label"
                    type="text"
                    value={firstElement.label}
                    onChange={(e) => onElementUpdate(firstElement.id, { label: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="connection-fill-type" className="text-xs">
                  Typ
                </Label>
                <Select
                  value={getCommonValue('fillType') || 'ring'}
                  onValueChange={(value: ConnectionFillType) => 
                    updateAllSelected({ fillType: value })
                  }
                >
                  <SelectTrigger id="connection-fill-type" className="h-8">
                    <SelectValue placeholder={getCommonValue('fillType') === undefined ? 'Mehrere Werte' : ''} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="filled">Gefüllter Kreis</SelectItem>
                    <SelectItem value="ring">Ring</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="connection-color" className="text-xs">
                  Farbe
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="connection-color"
                    type="color"
                    value={getCommonValue('color') || '#000000'}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="w-16 h-8"
                  />
                  <Input
                    type="text"
                    value={getCommonValue('color') || ''}
                    onChange={(e) => updateAllSelected({ color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    placeholder={getCommonValue('color') === undefined ? 'Mehrere Werte' : ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Durchmesser: {getCommonValue('diameter') !== undefined ? `${getCommonValue('diameter')}mm` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('diameter') || 10]}
                  onValueChange={([value]) => updateAllSelected({ diameter: value })}
                  min={2}
                  max={200}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Strichstärke: {getCommonValue('strokeWidth') !== undefined ? `${getCommonValue('strokeWidth')}px` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('strokeWidth') || 2]}
                  onValueChange={([value]) => updateAllSelected({ strokeWidth: value })}
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              </div>
            </>
          )}

          {/* Circle-specific properties */}
          {isSameType && firstElement.type === 'circle' && (
            <>
              <div className="space-y-2">
                <Label className="text-xs">
                  Radius: {getCommonValue('radius') !== undefined ? `${getCommonValue('radius')}` : 'Mehrere Werte'}
                </Label>
                <Slider
                  value={[getCommonValue('radius') || 1]}
                  onValueChange={([value]) => updateAllSelected({ radius: value })}
                  min={0.1}
                  max={10}
                  step={0.1}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="circle-filled" className="text-xs">
                  Gefüllt
                </Label>
                <Switch
                  id="circle-filled"
                  checked={getCommonValue('filled') ?? false}
                  onCheckedChange={(checked) => updateAllSelected({ filled: checked })}
                />
              </div>

              {getCommonValue('filled') && (
                <div className="space-y-2">
                  <Label htmlFor="circle-fill-color" className="text-xs">
                    Füllfarbe
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="circle-fill-color"
                      type="color"
                      value={getCommonValue('fillColor') || '#cccccc'}
                      onChange={(e) => updateAllSelected({ fillColor: e.target.value })}
                      className="w-16 h-8"
                    />
                    <Input
                      type="text"
                      value={getCommonValue('fillColor') || ''}
                      onChange={(e) => updateAllSelected({ fillColor: e.target.value })}
                      className="flex-1 h-8 text-xs"
                      placeholder={getCommonValue('fillColor') === undefined ? 'Mehrere Werte' : ''}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Mixed selection or unsupported multi-edit */}
          {!isSameType && (
            <div className="pt-2">
              <p className="text-xs text-gray-500">
                Verschiedene Elementtypen ausgewählt. Bitte wählen Sie Elemente des gleichen Typs für die Bearbeitung aus.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}